// roomMuteState — one app-wide copy of "which rooms have I muted, until when".
//
// Three surfaces need the same answer and none of them is a parent of the
// others: the rooms list (bell + dimmed badge), the open room's header, and
// the app-wide alert listener (which reads it inside a socket callback, not
// during a render). So the map lives in a module, is seeded once from
// `room_member_state`, and is written straight through when THIS tab mutes a
// room — the write's own realtime echo is not worth waiting for when the user
// just tapped "Mute · 8 hours".
//
// `muted_until` is an absolute instant, so "still muted?" is always a
// comparison against now, never a stored boolean.

import { useEffect, useState } from 'react';
import { fetchMyRoomMuteStates } from './roomActionsParity';

/** roomId → muted_until (epoch ms). A room absent from the map is unmuted. */
export type RoomMuteMap = Record<string, number>;

let mutes: RoomMuteMap = {};
const subscribers = new Set<(m: RoomMuteMap) => void>();

function publish(): void {
  const snapshot = mutes;
  for (const fn of subscribers) fn(snapshot);
}

export function getRoomMutes(): RoomMuteMap {
  return mutes;
}

/** Replace the whole map (the initial `room_member_state` read). */
export function seedRoomMutes(next: RoomMuteMap): void {
  mutes = next;
  publish();
}

/** This tab just muted (until = epoch ms) or unmuted (until = null) a room. */
export function setRoomMuteLocal(roomId: string, until: number | null): void {
  const next = { ...mutes };
  if (until == null) delete next[roomId];
  else next[roomId] = until;
  mutes = next;
  publish();
}

export function subscribeRoomMutes(fn: (m: RoomMuteMap) => void): () => void {
  subscribers.add(fn);
  return () => { subscribers.delete(fn); };
}

/** Is this room muted right now? */
export function isRoomMuted(roomId: string | null | undefined, nowMs: number = Date.now()): boolean {
  if (!roomId) return false;
  const until = mutes[roomId];
  return typeof until === 'number' && until > nowMs;
}

/** Test seam — drops the module state between tests. */
export function resetRoomMutes(): void {
  mutes = {};
  publish();
}

/**
 * React view of the map. Seeds it from the server ONCE per mount (a few rows,
 * no polling) and re-renders on every local change.
 */
export function useRoomMutes(enabled: boolean): RoomMuteMap {
  const [map, setMap] = useState<RoomMuteMap>(() => getRoomMutes());
  useEffect(() => {
    const off = subscribeRoomMutes(setMap);
    return off;
  }, []);
  useEffect(() => {
    if (!enabled) return;
    let dead = false;
    fetchMyRoomMuteStates()
      .then((m) => { if (!dead) seedRoomMutes(m); })
      // Background read: unmuted is the safe default — a missed suppression is
      // noise, a wrong one is a missed message.
      .catch(() => {});
    return () => { dead = true; };
  }, [enabled]);
  return map;
}

/** The label the header/rooms-list shows next to the bell. */
export function muteLabel(untilMs: number | null | undefined, nowMs: number = Date.now()): string {
  if (!untilMs || untilMs <= nowMs) return '';
  // "Always" is stored as a date far enough out that no human picked it.
  if (untilMs - nowMs > 365 * 24 * 60 * 60 * 1000) return 'Muted';
  const mins = Math.round((untilMs - nowMs) / 60000);
  if (mins < 60) return `Muted ${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `Muted ${hrs}h`;
  return `Muted ${Math.round(hrs / 24)}d`;
}

/** Mute durations offered in the room menu (Umer: 8 h / 1 week / always). */
export const MUTE_DURATIONS = [
  { label: '8 hours', ms: 8 * 60 * 60 * 1000 },
  { label: '1 week', ms: 7 * 24 * 60 * 60 * 1000 },
  // "Always" has to be an instant because the column is one; 100 years is
  // past every retention policy this company will ever have.
  { label: 'Always', ms: 100 * 365 * 24 * 60 * 60 * 1000 },
] as const;
